import React, {useEffect, useState} from 'react';
import {Platform, SafeAreaView, Linking, Alert} from 'react-native';
import Navigation from 'src/navigation';
import {useDispatch, useSelector} from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import sync from 'src/providers/watermelondb/sync';
import {checkLogin} from 'src/redux/auth';
import SVG from 'src/styles/svg';
import SplashScreen from 'react-native-splash-screen';
import {
  getTrackingStatus,
  TrackingStatus,
} from 'react-native-tracking-transparency';
import {requestTrackingPermission} from 'react-native-tracking-transparency/src';
import {Lottie} from 'src/design-system';
import {Separator} from 'src/common-components/atoms';
import {useDatabase} from '@nozbe/watermelondb/react';
import {useStyle} from 'src/providers/style';
import VersionCheck from 'react-native-version-check';
import {APP_VERSION} from 'react-native-dotenv';
import semver from 'semver';
import {usePermissionError} from 'src/contexts/PermissionErrorContext';
import {setPermissionErrorHandler} from 'src/providers/watermelondb/sync/permission-error-handler';
import {useSyncControl} from 'src/contexts/SyncContext';
import {
  acquireSyncLock,
  isSyncInProgress,
  releaseSyncLock,
} from 'src/common-utils/syncLock';

const Application = () => {
  const database = useDatabase();
  const dispatch = useDispatch();
  const styles = useStyle();
  const {selectedGroup} = useSelector(rootState => rootState.authentication);
  const [isLoading, setIsLoading] = useState(true);
  const [initialRoute, setInitialRoute] = useState('Login');
  const [splashHidden, setSplashHidden] = useState(false);
  const {syncDisabled} = useSyncControl();

  const {showError} = usePermissionError();

  useEffect(() => {
    setPermissionErrorHandler(showError);
  }, [showError]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setTimeout(() => {
        SplashScreen.hide();
        setSplashHidden(true);
      }, 1500);
    }
  }, []);

  useEffect(() => {
    if (!isLoading && !syncDisabled) {
      let interval = setInterval(async () => {
        if (!isSyncInProgress()) {
          const acquired = await acquireSyncLock({
            type: 'interval',
            partition: selectedGroup,
          });
          if (acquired) {
            try {
              await sync({database, partition: selectedGroup});
            } catch (error) {
              console.log('Interval sync error:', error);
            } finally {
              releaseSyncLock();
            }
          }
        }
      }, 10000);

      return () => {
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, syncDisabled]);

  useEffect(() => {
    if (splashHidden) {
      getTrackingStatus().then(async (value: TrackingStatus) => {
        if (value === 'not-determined') {
          await requestTrackingPermission();
        }
      });
    }
  }, [splashHidden]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const checkAppVersion = async () => {
        try {
          const latestVersion =
            Platform.OS === 'ios'
              ? await fetch(
                  'https://itunes.apple.com/in/lookup?bundleId=com.ravenhealth.app',
                )
                  .then(r => r.json())
                  .then(res => {
                    return res?.results[0]?.version;
                  })
              : await VersionCheck.getLatestVersion({
                  provider: 'playStore',
                  packageName: 'com.ravenhealth.app',
                  ignoreErrors: true,
                });

          if (semver.gt(latestVersion, APP_VERSION)) {
            Alert.alert(
              'Update Available',
              'A new version of the app is available.',
              [
                {
                  text: 'Update Now',
                  onPress: () => {
                    Linking.openURL(
                      Platform.OS === 'ios'
                        ? 'https://apps.apple.com/us/app/raven-health/id1571865814'
                        : 'https://play.google.com/store/apps/details?id=com.ravenhealth.app',
                    );
                  },
                },
                {
                  text: 'Close',
                  style: 'cancel',
                },
              ],
              {cancelable: true},
            );
          } else {
            // App is up-to-date; proceed with the app
          }
        } catch (error) {
          // Handle error while checking app version
          console.error('Error checking app version:', error);
        }
      };
      checkAppVersion();
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      if (await AsyncStorage.getItem('accessToken')) {
        sync({database, onInitialize: true}).catch(e => {
          console.log(e);
        });

        const response = await dispatch(checkLogin());
        if (response.payload.selectedGroup) {
          setInitialRoute('Dashboard');
        } else {
          setInitialRoute('OrganizationSelection');
        }
        setIsLoading(false);
      } else {
        setInitialRoute('Login');
        setIsLoading(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading ? (
        <SafeAreaView
          style={[
            styles.flex,
            styles.column,
            styles.alignCenter,
            styles.justifyCenter,
          ]}>
          <SVG height={30} name={'logo'} />
          <Separator height={40} />
          <Lottie src={require('../../assets/loading.json')} />
        </SafeAreaView>
      ) : (
        <>
          <SafeAreaView style={[styles.flex]}>
            <Navigation initialRoute={initialRoute} />
          </SafeAreaView>
        </>
      )}
    </>
  );
};

export default Application;
